// @flow
import ModularUIResponse from "../../modularui/ModularUIResponse";

import BaseModel from "./BaseModel";
import LinkCollection from "../links/LinkCollection";

import {
  IllegalArgumentException,
  IllegalStateException,
} from "../../exceptions";

import type LinkModel from "../links/LinkModel";
import type Href from "../href/Href";
import type { ModularUIModel, IModelWithChildModels } from "../types";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Base model, this the foundation for models that represent modular ui services, e.g. application, tab, caseview, etc
 */
class ResourceModel extends BaseModel implements IModelWithChildModels {
  _key: string;
  _locale: string;
  _childModels: Array<ModularUIModel>;
  _links: LinkCollection;
  _lastServerUpdate: number;

  /**
   * constructor
   */
  constructor(modularuiResponse: ModularUIResponse = new ModularUIResponse()) {
    super(
      modularuiResponse.data,
      modularuiResponse.contributions,
      modularuiResponse.modelOptions,
    );

    this._key = modularuiResponse.key;
    this._locale = modularuiResponse.locale;
    this._childModels = [];

    this._lastServerUpdate = Date.now();
  }

  /**
   * Property to render name of a model, is original name after transpilation
   */
  static get modelName(): string {
    throw new IllegalStateException(`No modelName set on the resource model`);
  }

  /**
   * Returns true when the model is supported based on configuration found in contributions
   *
   * @abstract
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    if (!(data instanceof ModularUIResponse)) {
      throw new IllegalArgumentException(
        "Given argument for isApplicableModel is not a ModularUIResponse",
      );
    }

    throw new Error("No isApplicableModel condition set on resourcemodel");
  }

  /**
   */
  get lastServerUpdate(): number {
    return this._lastServerUpdate;
  }

  /**
   */
  set lastServerUpdate(lastServerUpdate: number) {
    this._lastServerUpdate = lastServerUpdate;
  }

  /**
   */
  get locale(): string {
    return this._locale;
  }

  /**
   * Retrieve key
   */
  get key(): string {
    return this._key;
  }

  /**
   * Get type of model
   *
   * @abstract
   */
  get type(): string {
    throw new IllegalStateException(
      `No type set on the resource model with key ${this.key}`,
    );
  }

  /**
   * Retrieve type of resource
   */
  get resourcetype(): string {
    return this.getContribution("resourcetype", "unknown");
  }

  /**
   */
  get label(): string {
    return this.getContribution("label", "");
  }

  /**
   * Getting the links of the resource
   */
  get links(): LinkCollection {
    if (!this._links) {
      const linksData = Array.isArray(this.data._links)
        ? this.data._links[0]
        : this.data._links;

      const linksContrubtions = {
        self: {
          resourcetype: this.resourcetype,
        },
        ...this.contributions._links,
      };

      this._links = new LinkCollection(
        linksData,
        linksContrubtions,
        this.modelOptions,
      );
    }
    return this._links;
  }

  /**
   */
  set links(links: LinkCollection) {
    this._links = links;
  }

  /**
   *
   */
  get downloadLink(): ?LinkModel {
    const downloadLink = this.links.getLinksByGroup("download").first;
    if (downloadLink) {
      return downloadLink;
    }

    return null;
  }

  /**
   * Get self link of model
   */
  get selflink(): LinkModel {
    const selflink = this.links.getLinkByKey("self");

    if (selflink === null) {
      throw new IllegalStateException(
        `Could not find self link for ${
          this.key === null ? "unknown" : this.key
        }`,
      );
    }

    return selflink;
  }

  /**
   * Return default self link of resource
   */
  get selfhref(): Href {
    return this.selflink.href;
  }

  /**
   * Add links to expand on initialization of this model
   *
   * @abstract
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return [];
  }

  /**
   * Retrieve links of expanded child models
   */
  get childModels(): Array<ModularUIModel> {
    return this._childModels;
  }

  /**
   * Add child models to this model
   */
  addChildModels(
    models: Array<ModularUIModel> = [],
    errors: Array<ErrorResponse> = [],
  ): this {
    const flattenModels = models.flat();
    const flattenErrors = errors.flat();

    this._childModels = flattenModels;

    this.setChildModels(flattenModels, flattenErrors);

    return this;
  }

  /**
   * Template to set expanded child models
   * Use this hook to separate the retrieved child models into the correct models.
   *
   * @abstract
   * @example <caption>Put all models of instance List and GroupingPanel into the panels property</caption>
   */
  setChildModels(
    models: Array<ModularUIModel>,
    errors: Array<ErrorResponse>,
  ): void {
    if (!models && !errors) {
      throw new IllegalArgumentException("No models send to setChildModels");
    }
  }

  /**
   */
  dehydrate(): Object {
    return {
      ...super.dehydrate(),
      key: this._key,
      locale: this._locale,
      // $FlowFixMe[missing-type-arg]
      childModels: this._childModels.map<ModularUIModel>(
        (childModel: ModularUIModel) => childModel.dehydrate(),
      ),
    };
  }
}

export default ResourceModel;
